import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Power, X, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

const API = process.env.REACT_APP_BACKEND_URL;

const PATTERN_TYPES = [
  { value: 'exact',    label: 'Exakt' },
  { value: 'prefix',   label: 'Prefix' },
  { value: 'suffix',   label: 'Suffix' },
  { value: 'contains', label: 'Innehåller' },
  { value: 'regex',    label: 'Regex' },
];

const fmtDate = ts => ts
  ? new Date(ts).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

const emptyForm = {
  filename_pattern: '', pattern_type: 'contains',
  part_no: '', image_type: 'main', notes: '', is_active: true,
};

export default function ImageMappingsTab() {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const authHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token || ''}`,
    };
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/images/mappings`, { headers: await authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setMappings(data.mappings || []);
    } catch (e) {
      toast.error(`Kunde inte hämta mappningar: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.filename_pattern.trim() || !form.part_no.trim()) {
      toast.error('Mönster och PartNo krävs.');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/images/mappings`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${(await r.text()).slice(0, 150)}`);
      toast.success('Mappning skapad.');
      setShowForm(false);
      setForm(emptyForm);
      await load();
    } catch (e) {
      toast.error(`Spara misslyckades: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (m) => {
    try {
      const r = await fetch(`${API}/api/images/mappings/${m.id}`, {
        method: 'PATCH',
        headers: await authHeaders(),
        body: JSON.stringify({ is_active: !m.is_active }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMappings(prev => prev.map(x => x.id === m.id ? { ...x, is_active: !m.is_active } : x));
    } catch (e) {
      toast.error(`Kunde inte ändra status: ${e.message}`);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Ta bort mappningen "${m.filename_pattern}"?`)) return;
    try {
      const r = await fetch(`${API}/api/images/mappings/${m.id}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMappings(prev => prev.filter(x => x.id !== m.id));
      toast.success('Borttagen.');
    } catch (e) {
      toast.error(`Borttagning misslyckades: ${e.message}`);
    }
  };

  return (
    <div data-testid="image-mappings-tab">
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
      }}>
        <p style={{ fontSize: 13, color: '#6b7280', margin: 0, maxWidth: 600 }}>
          Mappningar används som fallback när filnamn inte kan parsas via standardreglerna
          (<code>_1</code>, <code>(2)</code>, <code>-3</code> etc). Prioritetsordning:
          <strong> exakt → prefix → suffix → innehåller → regex</strong>.
        </p>
        <button
          className="btn-primary"
          onClick={() => setShowForm(true)}
          data-testid="btn-add-mapping"
        >
          <Plus size={14} /> Lägg till mappning
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
          <Loader2 size={20} className="spin" /> Laddar...
        </div>
      ) : (
        <table className="data-table" data-testid="mappings-table">
          <thead>
            <tr>
              <th>Mönster</th>
              <th>Typ</th>
              <th>PartNo</th>
              <th>Bildtyp</th>
              <th style={{ textAlign: 'center' }}>Träffar</th>
              <th>Senast matchad</th>
              <th style={{ textAlign: 'center' }}>Aktiv</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {mappings.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                Inga mappningar än. Klicka på "Lägg till mappning" för att skapa en.
              </td></tr>
            )}
            {mappings.map(m => (
              <tr key={m.id} data-testid={`mapping-row-${m.id}`}
                  style={{ opacity: m.is_active ? 1 : 0.55 }}>
                <td><code>{m.filename_pattern}</code>
                  {m.notes && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{m.notes}</div>}
                </td>
                <td>{PATTERN_TYPES.find(p => p.value === m.pattern_type)?.label || m.pattern_type}</td>
                <td><code>{m.part_no}</code></td>
                <td>{m.image_type === 'main' ? 'Huvudbild' : 'Extra'}</td>
                <td style={{ textAlign: 'center' }}>{m.match_count || 0}</td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(m.last_matched_at)}</td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    onClick={() => toggleActive(m)}
                    data-testid={`mapping-toggle-${m.id}`}
                    title={m.is_active ? 'Inaktivera' : 'Aktivera'}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: m.is_active ? '#10b981' : '#9ca3af',
                    }}
                  >
                    <Power size={16} />
                  </button>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn-text"
                    onClick={() => remove(m)}
                    data-testid={`mapping-delete-${m.id}`}
                    title="Ta bort"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Lägg till-modal */}
      {showForm && (
        <div
          data-testid="mapping-form-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setShowForm(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', padding: 28, borderRadius: 12, width: 520,
              maxWidth: 'calc(100vw - 40px)', boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0 }}>Ny mappning</h3>
              <button
                onClick={() => setShowForm(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label className="form-label">Filnamnsmönster</label>
                <input
                  className="form-input"
                  value={form.filename_pattern}
                  onChange={e => setForm({ ...form, filename_pattern: e.target.value })}
                  placeholder="t.ex. BEEZ_jacket_red"
                  data-testid="form-pattern"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label className="form-label">Mönstertyp</label>
                  <select
                    className="form-input"
                    value={form.pattern_type}
                    onChange={e => setForm({ ...form, pattern_type: e.target.value })}
                    data-testid="form-pattern-type"
                  >
                    {PATTERN_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Bildtyp</label>
                  <select
                    className="form-input"
                    value={form.image_type}
                    onChange={e => setForm({ ...form, image_type: e.target.value })}
                    data-testid="form-image-type"
                  >
                    <option value="main">Huvudbild</option>
                    <option value="additional">Extra</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="form-label">PartNo (Norce)</label>
                <input
                  className="form-input"
                  value={form.part_no}
                  onChange={e => setForm({ ...form, part_no: e.target.value })}
                  placeholder="10001045-1150-10036"
                  data-testid="form-part-no"
                  style={{ fontFamily: 'monospace' }}
                />
              </div>

              <div>
                <label className="form-label">Anteckningar (valfritt)</label>
                <input
                  className="form-input"
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="t.ex. för fotograf-export från X"
                  data-testid="form-notes"
                />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })}
                  data-testid="form-is-active"
                />
                Aktiv direkt
              </label>
            </div>

            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn-secondary" onClick={() => setShowForm(false)} data-testid="form-cancel">
                Avbryt
              </button>
              <button
                className="btn-primary"
                onClick={create}
                disabled={saving}
                data-testid="form-save"
              >
                {saving ? <Loader2 size={14} className="spin" /> : null}
                Spara
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
